"use client";

import type { ReactNode } from "react";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import { formatCurrency } from "@tillix/utils/format";
import { observer } from "mobx-react-lite";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";
import type { PaymentStatus } from "./hooks/quick-pay-config";

interface QuickPayFormLayoutProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  onSubmit: (values: T) => void;
  rootError: string | undefined;
  paymentStatus: PaymentStatus;
  isSubmitting: boolean;
  isValid: boolean;
  amount: number;
  fee?: number;
  feeLabel?: string;
  onCancel: () => void;
  keyPrefix: string;
  children: ReactNode;
}

function QuickPayFormLayoutInner<T extends FieldValues>({
  form,
  onSubmit,
  rootError,
  paymentStatus,
  isSubmitting,
  isValid,
  amount,
  fee = 0,
  feeLabel,
  onCancel,
  keyPrefix,
  children,
}: QuickPayFormLayoutProps<T>) {
  const { t } = useT();
  const isBusy = paymentStatus !== "idle";
  const totalWithFee = amount + fee;

  return (
    <FormProvider {...form}>
      <Form
        className="flex flex-col gap-2"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {children}

        {fee > 0 && (
          <div className="flex items-center justify-between text-muted-foreground text-sm">
            <span>{feeLabel}</span>
            <span>{formatCurrency(fee)}</span>
          </div>
        )}

        <FormRootError keyPrefix={keyPrefix} rootError={rootError} />

        {(paymentStatus === "slow" || paymentStatus === "checking") && (
          <p className="text-center text-muted-foreground text-sm">
            {paymentStatus === "slow"
              ? t("quickPay.slowServers")
              : t("quickPay.checkingPaymentStatus")}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button
            disabled={isBusy}
            onClick={onCancel}
            type="button"
            variant="outline"
          >
            {t("application.back")}
          </Button>
          <Button disabled={isBusy || !isValid} type="submit">
            {paymentStatus === "checking"
              ? t("quickPay.checking")
              : paymentStatus === "slow" || isSubmitting
                ? t("application.processing")
                : t("quickPay.payAmount", {
                    amount: formatCurrency(totalWithFee),
                  })}
          </Button>
        </div>
      </Form>
    </FormProvider>
  );
}

const QuickPayFormLayout = observer(
  QuickPayFormLayoutInner,
) as typeof QuickPayFormLayoutInner;
export default QuickPayFormLayout;
